import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { resolveMutationErrorMessage } from '~/data/utils';

import { vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type ParsingCaseActionMutationResponse } from '../types';
import { zodParsingCaseActionResponseSchema } from '../validations';

const ERROR_MESSAGE_BY_STATUS: Record<number, () => string> = {
  404: () =>
    i18n.t(($) => $.retryParsingCase.error404, {
      ns: 'data',
    }),
  409: () =>
    i18n.t(($) => $.retryParsingCase.error409, {
      ns: 'data',
    }),
  422: () =>
    i18n.t(($) => $.retryParsingCase.error422, {
      ns: 'data',
    }),
};

/**
 * Retry a parsing case from start (full pipeline: OCR → enrich → validate → parse → process).
 * Reuses the source job's RAW blob; allowed regardless of the case's current status.
 *
 * @param caseId - UUID of the parsing case to retry
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Parsing-Cases/operation/post_retry_case_vadmin_parsing_cases__case_id__actions_retry_post
 */
export const retryParsingCase = async (
  caseId: UUID,
): Promise<ParsingCaseActionMutationResponse> => {
  try {
    return await vestigasApi
      .post(ENDPOINT.PARSING_CASE.RETRY(caseId), {
        validate: { response: zodParsingCaseActionResponseSchema },
      })
      .json<ParsingCaseActionMutationResponse>();
  } catch (error) {
    log.error({
      message: 'Error retrying parsing case',
      error,
    });

    const errorMessage = resolveMutationErrorMessage(
      error,
      ERROR_MESSAGE_BY_STATUS,
      () =>
        i18n.t(($) => $.retryParsingCase.error.generic, {
          ns: 'data',
        }),
    );
    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    throw error;
  }
};
